if (!customElements.get('option-combobox')) {
	// Trade re-renders the picker after a choice, possibly more than once.
	const REFOCUS_MS = 3000
	let refocus = { id: null, until: 0 }

	customElements.define(
		'option-combobox',
		class OptionCombobox extends HTMLElement {
			connectedCallback() {
				this.input = this.querySelector('[role="combobox"]')
				this.listbox = this.querySelector('[role="listbox"]')
				this.select = this.querySelector('select')
				this.status = this.querySelector('[data-status]')
				this.options = Array.from(this.listbox.querySelectorAll('[role="option"]'))
				this.selected = this.input.value

				this.input.addEventListener('input', () => this.filter(this.input.value, true))
				this.input.addEventListener('keydown', event => this.onKeydown(event))
				this.input.addEventListener('click', () => this.toggle(this.listbox.hidden))
				this.input.addEventListener('blur', () => this.close(true))
				this.listbox.addEventListener('mousedown', event => event.preventDefault())
				this.listbox.addEventListener('click', event => {
					const option = event.target.closest('[role="option"]')
					if (option) this.choose(option)
				})

				if (refocus.id === this.input.id && Date.now() < refocus.until) {
					requestAnimationFrame(() => this.input.focus())
				}
			}

			get visible() {
				return this.options.filter(option => !option.hidden)
			}

			get active() {
				return this.options.find(option => option.classList.contains('is-active'))
			}

			filter(query, open) {
				const needle = query.trim().toLowerCase()
				this.options.forEach(option => {
					option.hidden = needle !== '' && !option.dataset.value.toLowerCase().includes(needle)
				})
				this.activate(this.visible[0])
				if (open) this.open()
				this.announce()
			}

			announce() {
				this.status.textContent = this.dataset.statusTemplate.replace(
					'[count]',
					this.visible.length,
				)
			}

			open() {
				this.listbox.hidden = false
				this.input.setAttribute('aria-expanded', 'true')
				if (!this.active) {
					this.activate(
						this.options.find(o => o.getAttribute('aria-selected') === 'true') ?? this.visible[0],
					)
				}
			}

			close(restore) {
				this.listbox.hidden = true
				this.input.setAttribute('aria-expanded', 'false')
				this.input.removeAttribute('aria-activedescendant')
				if (restore) {
					this.input.value = this.selected
					this.options.forEach(option => (option.hidden = false))
				}
			}

			toggle(open) {
				if (open) {
					this.filter('', true)
				} else {
					this.close(true)
				}
			}

			activate(option) {
				this.options.forEach(o => o.classList.toggle('is-active', o === option))
				if (option) {
					this.input.setAttribute('aria-activedescendant', option.id)
					option.scrollIntoView({ block: 'nearest' })
				} else {
					this.input.removeAttribute('aria-activedescendant')
				}
			}

			move(step) {
				const visible = this.visible
				if (visible.length === 0) return
				if (this.listbox.hidden) this.open()
				const at = visible.indexOf(this.active)
				this.activate(visible[(at + step + visible.length) % visible.length])
			}

			onKeydown(event) {
				switch (event.key) {
					case 'ArrowDown':
						event.preventDefault()
						if (event.altKey) return this.open()
						return this.move(1)
					case 'ArrowUp':
						event.preventDefault()
						return this.move(-1)
					case 'Home':
					case 'End':
						if (this.listbox.hidden) return
						event.preventDefault()
						return this.activate(event.key === 'Home' ? this.visible[0] : this.visible.at(-1))
					case 'Enter':
						if (this.listbox.hidden || !this.active) return
						event.preventDefault()
						return this.choose(this.active)
					case 'Escape':
						if (this.listbox.hidden) {
							this.input.value = ''
							return this.filter('', false)
						}
						event.preventDefault()
						return this.close(true)
					case 'Tab':
						return this.close(true)
				}
			}

			choose(option) {
				const { value } = option.dataset
				this.selected = value
				this.close(true)
				if (this.select.value === value) return
				this.options.forEach(o => o.setAttribute('aria-selected', String(o === option)))
				this.select.value = value
				refocus = { id: this.input.id, until: Date.now() + REFOCUS_MS }
				this.select.dispatchEvent(new Event('change', { bubbles: true }))
			}
		},
	)
}
